// search.js v2026-06-22c — adds enrichWithCollectionProducts (pulls matched collection's products into dropdown)
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button.search__reset');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    this.performSearch();
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    if (this.getQuery().length > 0) {
      this.performSearch();
    }
  }

  normalizeModelNumber(term) {
    return term.replace(/([a-zA-Z])(\d)/g, '$1 $2');
  }

  buildSearchURL(searchTerm) {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let term = searchTerm;
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(term)}`;
    }
    url.searchParams.set('q', term);
    // Include collections/pages/articles so printer family pages surface in SAYT,
    // not just products. The predictive-search template already renders these blocks.
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  setupURL() {
    return this.buildSearchURL(this.getQuery());
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.performSearch();
  }

  performSearch() {
    const raw = this.getQuery();
    const normalized = this.normalizeModelNumber(raw);
    const needsMerge = normalized !== raw;
    // Remembered so the "no results" heading can show the user's actual input
    // instead of the space-normalized variant (e.g. "C530dn" not "C 530dn").
    this.lastRawQuery = raw;
    this.lastNormalizedQuery = normalized;

    if (!needsMerge) {
      const url = this.buildSearchURL(raw).toString();
      this.renderSection(url);
      return;
    }

    const cacheKey = 'merged:' + raw;
    if (this.cachedMap.has(cacheKey)) {
      this.renderSearchResults(this.cachedMap.get(cacheKey));
      this.setAttribute('results', '');
      return;
    }

    this.setLoadingState(true);

    const normalizedUrl = this.buildSearchURL(normalized).toString();
    const rawUrl = this.buildSearchURL(raw).toString();

    // Raw goes first so it's the merge primary — when only collections/articles
    // (not products) match, mergeProductResults falls back to the primary HTML,
    // and we want that to be the raw query's HTML, not the space-normalized one.
    Promise.all([
      fetch(rawUrl).then(r => r.ok ? r.text() : ''),
      fetch(normalizedUrl).then(r => r.ok ? r.text() : ''),
    ])
      .then(([rawHTML, normalizedHTML]) => {
        const merged = this.mergeProductResults(rawHTML, normalizedHTML);
        this.renderSearchResults(merged);
        this.cachedMap.set(cacheKey, merged);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  mergeProductResults(primaryHTML, secondaryHTML) {
    if (!secondaryHTML) return primaryHTML;
    if (!primaryHTML) return secondaryHTML;

    const parser = new DOMParser();
    const primaryDoc = parser.parseFromString(primaryHTML, 'text/html');
    const secondaryDoc = parser.parseFromString(secondaryHTML, 'text/html');

    const primaryList = primaryDoc.querySelector('#predictive-search-results-products-list');
    const secondaryList = secondaryDoc.querySelector('#predictive-search-results-products-list');

    if (!primaryList && !secondaryList) return primaryHTML;
    if (!primaryList) return secondaryHTML;
    if (!secondaryList) return primaryHTML;

    const existingHrefs = new Set();
    primaryList.querySelectorAll('a[href*="/products/"]').forEach(a => {
      existingHrefs.add(a.getAttribute('href').split('?')[0]);
    });

    Array.from(secondaryList.children).forEach(child => {
      const link = child.querySelector('a[href*="/products/"]');
      if (link) {
        const href = link.getAttribute('href').split('?')[0];
        if (!existingHrefs.has(href)) {
          existingHrefs.add(href);
          primaryList.appendChild(child.cloneNode(true));
        }
      }
    });

    return primaryDoc.documentElement.outerHTML;
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.rewriteEmptyStateTerm(targetElement);
        this.enrichWithCollectionProducts(targetElement);

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  rewriteEmptyStateTerm(targetElement) {
    if (!this.lastRawQuery || !this.lastNormalizedQuery) return;
    if (this.lastRawQuery === this.lastNormalizedQuery) return;
    const heading = targetElement.querySelector('.empty-state h4');
    if (!heading) return;
    if (heading.textContent.indexOf(this.lastNormalizedQuery) === -1) return;
    heading.textContent = heading.textContent.replace(this.lastNormalizedQuery, this.lastRawQuery);
  }

  // When the query matched a collection (e.g. printer family page) but no products,
  // pull the top-matched collection's products and surface them in the dropdown.
  // Covers cases where customers search a printer model but products only carry the
  // base SKU (e.g. searching "C530dn" — products are tagged "C530" not "C530dn").
  enrichWithCollectionProducts(targetElement) {
    if (targetElement.querySelector('#predictive-search-results-products-list')) return;
    const firstCollectionLink = targetElement.querySelector('.predictive-search-result--collections a[href*="/collections/"]');
    if (!firstCollectionLink) return;

    const href = firstCollectionLink.getAttribute('href');
    const m = href.match(/\/collections\/([^/?]+)/);
    if (!m) return;
    const collectionHandle = m[1];
    const collectionTitle = firstCollectionLink.textContent.trim();
    const queryAtFetch = this.getQuery();
    const cacheKey = 'coll-products:' + collectionHandle;

    const render = (products) => {
      if (this.getQuery() !== queryAtFetch) return;
      if (!products || !products.length) return;
      const html = this.buildCollectionProductsHTML(products, collectionHandle, collectionTitle);
      const leftColumn = targetElement.querySelector('.search__box-products');
      const emptyState = targetElement.querySelector('.empty-state');
      if (leftColumn) {
        leftColumn.insertAdjacentHTML('afterbegin', html);
      } else if (emptyState) {
        emptyState.outerHTML = html;
      } else {
        targetElement.insertAdjacentHTML('afterbegin', html);
      }
    };

    if (this.cachedMap.has(cacheKey)) {
      render(this.cachedMap.get(cacheKey));
      return;
    }

    fetch('/collections/' + encodeURIComponent(collectionHandle) + '/products.json?limit=6')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const products = (data && data.products) || [];
        this.cachedMap.set(cacheKey, products);
        render(products);
      })
      .catch(() => {});
  }

  buildCollectionProductsHTML(products, collectionHandle, collectionTitle) {
    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const fmt = (val) => {
      if (val === undefined || val === null || val === '') return '';
      const n = Number(val);
      if (isNaN(n)) return '';
      return '$' + n.toFixed(2);
    };
    const sizedImage = (url) => {
      if (!url) return '';
      return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=300';
    };

    const cards = products.map((p) => {
      const imgSrc = sizedImage(p.images && p.images[0] && p.images[0].src);
      const price = fmt(p.variants && p.variants[0] && p.variants[0].price);
      const title = escapeHtml(p.title);
      const handle = encodeURIComponent(p.handle || '');
      return (
        '<div class="f-column">' +
          '<a href="/products/' + handle + '" class="block reversed-link" style="text-decoration:none;color:inherit;">' +
            '<div style="aspect-ratio:1;background:#f5f5f5;overflow:hidden;border-radius:8px;margin-bottom:8px;display:flex;align-items:center;justify-content:center;">' +
              (imgSrc ? '<img src="' + escapeHtml(imgSrc) + '" alt="' + title + '" loading="lazy" style="width:100%;height:100%;object-fit:contain;">' : '') +
            '</div>' +
            '<div class="text-sm" style="font-weight:500;line-height:1.3;margin-bottom:4px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">' + title + '</div>' +
            (price ? '<div class="text-sm" style="font-weight:700;">' + price + '</div>' : '') +
          '</a>' +
        '</div>'
      );
    }).join('');

    const headingText = 'Products in ' + escapeHtml(collectionTitle);
    const buttonText = 'View all products in ' + escapeHtml(collectionTitle);

    return (
      '<div class="flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products" data-collection-products>' +
        '<h4 class="h4 predictive-search-result__heading">' + headingText + '</h4>' +
        '<div class="swipe-mobile swipe-mobile--2-cols">' +
          '<div id="predictive-search-results-products-list" class="f-grid f-grid--gap-sm f-grid--row-gap-sm swipe-mobile__inner">' +
            cards +
          '</div>' +
        '</div>' +
        '<div class="search__results-all block">' +
          '<a href="/collections/' + encodeURIComponent(collectionHandle) + '" class="btn btn--primary">' +
            '<span class="btn__text">' + buttonText + '</span>' +
          '</a>' +
        '</div>' +
      '</div>'
    );
  }

  handleClickOutside(event) {
    const target = event.target;
    const isCloseButton = target.classList.contains('header__search-close') || target.closest('.header__search-close');
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          isCloseButton)
      : !this.contains(target);

    if (shouldClose) {
      if (isCloseButton) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
